import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { History, Trash2, CheckCircle, Loader2, Sparkles, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PromptHistory = ({ botId }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingId, setApplyingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPrompts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/bot/prompt-history`, { withCredentials: true });
      setPrompts(data.prompts || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const handleApply = async (prompt, promptId) => {
    setApplyingId(promptId);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/bot/apply-prompt`,
        { prompt, bot_id: botId }, { withCredentials: true }
      );
      if (data.success) {
        toast.success('Prompt aplicado a tu bot');
        fetchPrompts();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aplicar');
    } finally {
      setApplyingId(null);
    }
  };

  const handleDelete = async (promptId) => {
    setDeletingId(promptId);
    try {
      await axios.delete(`${BACKEND_URL}/api/bot/prompt-history/${promptId}`, { withCredentials: true });
      setPrompts(prev => prev.filter(p => p.prompt_id !== promptId));
      toast.success('Prompt eliminado');
    } catch {
      toast.error('Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Cargando historial...</span>
        </div>
      </div>
    );
  }

  if (prompts.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="prompt-history-section">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <History className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-950">Historial de Prompts</h3>
            <p className="text-xs text-gray-500">{prompts.length} prompt{prompts.length !== 1 ? 's' : ''} generado{prompts.length !== 1 ? 's' : ''} por Alex</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
        {prompts.map((item) => (
          <div key={item.prompt_id} className="px-6 py-4 hover:bg-gray-50/50 transition-colors" data-testid={`prompt-item-${item.prompt_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                  {item.applied && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <CheckCircle className="w-3 h-3" />Aplicado
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    <Sparkles className="w-3 h-3" />Alex
                  </span>
                </div>
                <p className="text-sm text-gray-700 line-clamp-3 font-mono leading-relaxed bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  {item.prompt.length > 300 ? item.prompt.substring(0, 300) + '...' : item.prompt}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 pt-1">
                <Button size="sm" onClick={() => handleApply(item.prompt, item.prompt_id)}
                  disabled={applyingId === item.prompt_id}
                  data-testid={`apply-history-prompt-${item.prompt_id}`}
                  className="bg-indigo-600 text-white hover:bg-indigo-700 text-xs px-3 py-1.5 h-auto rounded-lg">
                  {applyingId === item.prompt_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <><CheckCircle className="w-3 h-3 mr-1" />Aplicar</>
                  )}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(item.prompt_id)}
                  disabled={deletingId === item.prompt_id}
                  data-testid={`delete-history-prompt-${item.prompt_id}`}
                  className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-auto p-1.5 rounded-lg">
                  {deletingId === item.prompt_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromptHistory;
